import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'See Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function FloorPlansIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const cta = useSlotGroup('floorplansintro.cta', ctaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-16 md:pb-24"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="floorplansintro.heading"
          label="Heading"
          defaultValue='<p>Experience <span data-type="field-token" data-field="property_name"></span> Living</p>'
          as="h2"
          className="font-heading text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] font-semibold uppercase tracking-wide text-[var(--vibe-color-primary)] leading-[1.1] mb-6"
        />

        <div className="max-w-3xl mx-auto">
          <RichTextContent
            path="floorplansintro.body"
            label="Body Text"
            defaultField="property_floorplans_blurb"
            defaultValue={`<p>At <span data-type="field-token" data-field="property_name"></span>, we offer a variety of spacious floor plans designed to fit your lifestyle. Whether you're looking for a cozy studio or a more expansive studio-3 bedroom apartment, each layout features modern finishes and plenty of natural light. With top-tier amenities and a prime location, you're sure to find the perfect space. Ready to find your dream apartment? Apply today and make <span data-type="field-token" data-field="property_name"></span> your new place to call home!</p>`}
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div className="mt-6 flex justify-center">
          <EditableSlotGroup groupPath="floorplansintro.cta" definition={ctaDef}>
            <a
              href={cta.url || '/amenities'}
              target={cta._target || '_self'}
              className="fpi-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
            >
              {cta.label || 'See Amenities'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .fpi-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .fpi-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansIntro',
  displayName: 'Floor Plans Intro',
  tokens: {
    'floorplansintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>Experience <span data-type="field-token" data-field="property_name"></span> Living</p>',
    },
    'floorplansintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_floorplans_blurb',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we offer a variety of spacious floor plans designed to fit your lifestyle. Whether you\'re looking for a cozy studio or a more expansive studio-3 bedroom apartment, each layout features modern finishes and plenty of natural light. With top-tier amenities and a prime location, you\'re sure to find the perfect space. Ready to find your dream apartment? Apply today and make <span data-type="field-token" data-field="property_name"></span> your new place to call home!</p>',
    },
  },
  slotGroups: {
    'floorplansintro.cta': ctaDef,
  },
};

(FloorPlansIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansIntro;
